import { ArrowUpRight } from 'lucide-react';
import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { Premize3D } from '@/components/landing/premize-3d/Premize3D';
import { PremizeInside } from '@/components/home/PremizeInside';
import { STATS, TERMS, measuredCaption } from '@/content/premize';
import { premizeHref } from '@/content/site';

/**
 * The product, and the loudest section after the hero: its own ground (the
 * page's one band), "complex inside, simple on top" told in 3D (the box opens,
 * its six layers lift out, the standout features play above it), then the
 * measured numbers at display size. The only place on the page the numbers
 * appear; the lime units are this section's one accent.
 */
export function PremizeSection() {
  return (
    <Plate eyebrow="The product" id="premize" labelledBy="premize-title" band>
      <img
        loading="lazy"
        decoding="async"
        src="/img/premize-logo.webp"
        width={640}
        height={166}
        alt="PreMize, Efficient Local LLM"
        className="lp-reveal h-12 w-auto"
        style={stagger(1)}
      />
      <h2 id="premize-title" className="lp-h2 lp-reveal mt-10 max-w-4xl" style={stagger(2)}>
        Private AI in one box.
      </h2>
      <p className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(3)}>
        PreMize is our ready-to-run AI system on NVIDIA DGX Spark. Upload your documents and ask questions in
        plain language. Every answer shows the page it came from.
      </p>

      <div className="lp-reveal mt-16 md:mt-24" style={stagger(4)}>
        <h3 className="t-h3-xl max-w-3xl">Complex inside. Simple on top.</h3>
        <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-[hsl(var(--fg-secondary))]">
          Six layers, each built by specialists, packed into one box on your desk. All your team sees is the
          answer.
        </p>
        <div className="mt-10">
          <Premize3D>
            <PremizeInside />
          </Premize3D>
        </div>
      </div>

      <dl className="lp-reveal mt-16 grid gap-10 sm:grid-cols-3 sm:gap-8 md:mt-24" style={stagger(5)}>
        {STATS.map((s) => (
          <div key={s.label} className="border-t border-[hsl(var(--border-strong))] pt-6">
            <dt className="sr-only">{s.label}</dt>
            <dd>
              <span className="t-stat-xl">{s.value}</span>
              {s.unit && (
                <span className="ml-2 text-[clamp(1.25rem,1rem+0.7vw,1.75rem)] font-medium tracking-tight text-primary">
                  {s.unit}
                </span>
              )}
              <span className="mt-4 block max-w-[18rem] text-[17px] leading-snug text-[hsl(var(--fg-secondary))]" aria-hidden>
                {s.label}
              </span>
            </dd>
          </div>
        ))}
      </dl>
      <p className="t-caption lp-reveal mt-6" style={stagger(6)}>
        {measuredCaption()}
      </p>

      <dl className="mt-20 grid gap-x-10 gap-y-8 border-t border-border pt-12 sm:grid-cols-2 lg:grid-cols-4">
        {TERMS.map((t, i) => (
          <div key={t.title} className="lp-reveal" style={stagger(7 + i)}>
            <dt className="t-h3 text-xl">{t.title}</dt>
            <dd className="lp-desc mt-2">{t.line}</dd>
          </div>
        ))}
      </dl>

      <div className="lp-reveal mt-14" style={stagger(11)}>
        <a href={premizeHref('premize')} target="_blank" rel="noopener noreferrer" className="btn-primary">
          Explore PreMize <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </Plate>
  );
}
